import { useState, useRef, useEffect } from "react";
import axiosInstance from "../../API/interceptor";
import PersonTasksPopup from "./PersonTasksPopup";
import { PhotoAvatar } from "./PersonCell";


export default function PersonRaciPopup({ name, personId, anchorRect, cancelHide, scheduleHide }) {
  const [showTasks,  setShowTasks]  = useState(false);
  const [activeRaci, setActiveRaci] = useState(null);
  const [raciCounts, setRaciCounts] = useState({ R: 0, A: 0, C: 0, I: 0 });
  const [wsCount,    setWsCount]    = useState(0);
  const [loading,    setLoading]    = useState(true);

  const popupRef   = useRef();
  const [popupRect, setPopupRect] = useState(null);
  const tasksTimer = useRef(null);
  const openTimer   = useRef(null);
  const raciOpenTimer = useRef(null);

  useEffect(() => {
    if (!personId) return;
    setLoading(true);
    axiosInstance
      .post("/task_assignment/get_person_raci_counts", { person_id: personId })
      .then(res => {
        const d = res.data?.counts || res.data || {};
        setRaciCounts({
          R: d.responsible || 0,
          A: d.accountable || 0,
          C: d.consulted   || 0,
          I: d.informed    || 0,
        });
        setWsCount(d.total_products || d.workspace_count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [personId]);

  const raciStats = [
    { key:"R", label:"RESPONSIBLE", color:"#16A34A", accentBorder:"#16A34A", bg:"#F0FDF4", count: raciCounts.R },
    { key:"A", label:"ACCOUNTABLE", color:"#2563EB", accentBorder:"#2563EB", bg:"#EFF6FF", count: raciCounts.A },
    { key:"C", label:"CONSULTED",   color:"#D97706", accentBorder:"#D97706", bg:"#FFFBEB", count: raciCounts.C },
    { key:"I", label:"INFORMED",    color:"#7C3AED", accentBorder:"#7C3AED", bg:"#F5F3FF", count: raciCounts.I },
  ];

  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const above      = spaceBelow < 260;
  const left       = Math.max(8, Math.min(anchorRect.left + 50, window.innerWidth - 420));
  const top        = above ? anchorRect.top - 6 : anchorRect.bottom + 6;

  const cancelAll = () => {
    cancelHide();
    clearTimeout(tasksTimer.current);
    clearTimeout(openTimer.current);
    clearTimeout(raciOpenTimer.current);
  };

  const scheduleCloseAll = () => {
    clearTimeout(raciOpenTimer.current);
    clearTimeout(openTimer.current);
    tasksTimer.current = setTimeout(() => { setShowTasks(false); setActiveRaci(null); }, 400);
    if (showTasks) {
      openTimer.current = setTimeout(() => scheduleHide(), 500);
    } else {
      scheduleHide();
    }
  };

  const handlePopupEnter = () => cancelAll();
  const handlePopupLeave = () => scheduleCloseAll();
  const handleTasksEnter = () => cancelAll();
  const handleTasksLeave = () => scheduleCloseAll();

  return (
    <>
      <div
        ref={popupRef}
        onMouseEnter={handlePopupEnter}
        onMouseLeave={handlePopupLeave}
        className={`fixed bg-white rounded-2xl border border-slate-200 shadow-[0_20px_60px_rgba(109,40,217,.2),0_4px_16px_rgba(0,0,0,.1)] pointer-events-auto overflow-hidden z-[2147483647] w-[400px] ${
          above ? "-translate-y-full" : ""
        }`}
        style={{ left, top }}
      >
        <div className="px-4 py-4 flex items-center justify-between bg-[linear-gradient(135deg,#7C3AED,#6D28D9)]">
          <div className="flex items-center gap-3">
            <PhotoAvatar name={name} userId={personId} size={40} />
            <div>
              <div className="text-base font-black text-white">{name}</div>
              <div className="text-[11px] mt-0.5 text-white/65">
                Hover card to see workspace breakdown
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/[.18] border border-white/25">
            <span className="text-xs font-bold text-white">Workspace: {wsCount}</span>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2.5 p-4">
          {loading ? (
            <div className="col-span-4 text-center text-xs py-4 text-[#94A3B8]">
              Loading...
            </div>
          ) : raciStats.map(r => (
            <div
              key={r.key}
              onMouseEnter={() => {
                cancelAll();
                if (showTasks) {
                  setActiveRaci(r.key);
                  if (popupRef.current) setPopupRect(popupRef.current.getBoundingClientRect());
                } else {
                  raciOpenTimer.current = setTimeout(() => {
                    if (popupRef.current) setPopupRect(popupRef.current.getBoundingClientRect());
                    setActiveRaci(r.key);
                    setShowTasks(true);
                  }, 400);
                }
              }}
              onMouseLeave={() => {
                clearTimeout(raciOpenTimer.current);
              }}
              className="flex flex-col items-center justify-center py-4 px-2 rounded-2xl cursor-pointer relative overflow-hidden transition-all duration-150"
              style={{
                background: activeRaci === r.key ? r.accentBorder + "22" : r.bg,
                border: `2px solid ${r.accentBorder}`,
                transform: activeRaci === r.key ? "scale(1.06)" : "scale(1)",
                boxShadow: activeRaci === r.key ? `0 6px 18px ${r.accentBorder}44` : "none",
              }}
            >
              <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
                style={{ background: r.accentBorder }} />
              <span className="text-xl font-black text-gray-700 mb-1.5 mt-1">{r.key}</span>
              <span className="text-3xl font-black leading-none mb-2" style={{ color: r.color }}>
                {r.count}
              </span>
              <span className="text-[9px] font-bold tracking-widest text-center opacity-80"
                style={{ color: r.color }}>
                {r.label}
              </span>
            </div>
          ))}
        </div>

        <div className="px-4 pb-3 flex items-center justify-center">
          <span className="text-xs text-slate-400">Hover this card to see workspace breakdown</span>
        </div>
      </div>

      {showTasks && activeRaci && popupRect && (
        <PersonTasksPopup
          name={name}
          personId={personId}
          activeRaci={activeRaci}
          raciLeft={left}
          raciWidth={400}
          raciTop={popupRect.top}
          cancelHide={handleTasksEnter}
          scheduleHide={handleTasksLeave}
        />
      )}
    </>
  );
}